import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { fetchCard, type RetrievedCard } from '@/lib/api'
import { supabase } from '@/lib/supabase'

console.log('[f-store] Module loading...')

// =============================================================================
// User isolation check - runs SYNCHRONOUSLY before store hydration
// =============================================================================

const F_USER_ID_KEY = 'f-store-user-id'

function clearStoreIfUserMismatch(): void {
  try {
    const storedUserId = localStorage.getItem(F_USER_ID_KEY)
    const hasFStore = localStorage.getItem('f-store') !== null

    console.log('[f-store] SYNC: Checking user mismatch')

    let currentUserId: string | null = null

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        try {
          const rawValue = localStorage.getItem(key)
          const authData = JSON.parse(rawValue || '{}')
          currentUserId = authData?.user?.id || authData?.session?.user?.id || null
          if (currentUserId) break
        } catch {
          // Invalid JSON, skip
        }
      }
    }

    if (storedUserId && currentUserId && storedUserId !== currentUserId) {
      console.log('[f-store] SYNC: User mismatch detected! Clearing stale data')
      localStorage.removeItem('f-store')
      localStorage.setItem(F_USER_ID_KEY, currentUserId)
    } else if (!storedUserId && hasFStore && currentUserId) {
      console.log('[f-store] SYNC: No stored user ID but f-store exists. Clearing orphaned data.')
      localStorage.removeItem('f-store')
      localStorage.setItem(F_USER_ID_KEY, currentUserId)
    } else if (!storedUserId && currentUserId) {
      console.log('[f-store] SYNC: New user, storing user ID')
      localStorage.setItem(F_USER_ID_KEY, currentUserId)
    } else if (!currentUserId && hasFStore) {
      console.log('[f-store] SYNC: Clearing orphaned f-store (no logged in user)')
      localStorage.removeItem('f-store')
      localStorage.removeItem(F_USER_ID_KEY)
    }
  } catch (e) {
    console.error('[f-store] Error checking user mismatch:', e)
  }
}

clearStoreIfUserMismatch()

// =============================================================================
// API Functions
// =============================================================================

const API_BASE = '/api'

async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}
}

// Session types
export interface FSessionListItem {
  session_id: string
  name: string | null
  status: string
  total_turns: number
  entity_name: string | null
  entity_type: string | null
  created_at: string
  last_message_at: string | null
}

export interface FSession extends FSessionListItem {
  foolscap_json: FoolscapJson | null
  foolscap_markdown: string | null
  foolscap_markdown_annotated: string | null
  session_context: Record<string, unknown> | null
  integrated_card_ids: string[] | null
  surfaced_card_ids: string[] | null
  updated_at: string
}

export interface FSessionTurn {
  turn_id: string
  turn_number: number
  role: 'user' | 'assistant'
  content: string
  thinking: string | null
  cards_surfaced: string[] | null
  cards_used: Record<string, unknown> | null
  document_changes: Record<string, unknown> | null
  timestamp: string
}

export interface FSessionWithTurns extends FSession {
  turns: FSessionTurn[]
}

// Foolscap document structure (matches exact markdown format)
export interface FoolscapJson {
  title?: string
  entity?: string
  macro_mission?: string
  spine?: string
  act_1?: {
    assets?: string
    identity?: string
    deficiency?: string
    mission_alignment?: string
    tension?: string
    tension_external?: string
    tension_internal?: string
    climax_embedded?: string
  }
  act_2?: {
    operations?: string[]
    development?: string[]
    resistance?: string
    breakthrough?: string
    asset_shifts?: string
  }
  act_3?: {
    the_strike?: string
    resolution?: string
    new_assets?: string
    transformation?: string
    mission_check?: string
  }
  narrative_beats?: string[]
  cards_integrated?: string[]
}

// API functions
async function listFSessions(
  status = 'active',
  limit = 50,
  offset = 0
): Promise<FSessionListItem[]> {
  const params = new URLSearchParams({ status, limit: String(limit), offset: String(offset) })
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/f/sessions?${params}`, { headers: authHeaders })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to list sessions: ${response.statusText}`)
  }
  return response.json()
}

async function createFSession(
  name?: string,
  entityName?: string,
  entityType?: string
): Promise<FSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/f/sessions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ name, entity_name: entityName, entity_type: entityType }),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to create session: ${response.statusText}`)
  }
  return response.json()
}

async function getFSessionWithTurns(sessionId: string): Promise<FSessionWithTurns> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/f/sessions/${sessionId}/full`, { headers: authHeaders })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to get session: ${response.statusText}`)
  }
  return response.json()
}

export async function updateFSession(
  sessionId: string,
  updates: {
    name?: string
    status?: string
    entity_name?: string
    entity_type?: string
    foolscap_json?: FoolscapJson
    foolscap_markdown?: string
  }
): Promise<FSession> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/f/sessions/${sessionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(updates),
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to update session: ${response.statusText}`)
  }
  return response.json()
}

async function deleteFSession(sessionId: string): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/f/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: authHeaders,
  })
  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      throw new Error('Authentication required')
    }
    throw new Error(`Failed to delete session: ${response.statusText}`)
  }
}

// =============================================================================
// Types
// =============================================================================

export interface Turn {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  thinking?: string
  cardsSurfaced?: string[]
  cardsUsed?: { integrated?: string[]; referenced?: string[] }
  // Per-node thinking (for collapsible drawers)
  evaluatorThinking?: string
  editorThinking?: string
  responderThinking?: string
  // Node execution flags
  evaluatorRan?: boolean
  editorRan?: boolean
  responderRan?: boolean
}

export interface ComposerResponse {
  response: string
  thinking?: string
  document_updates?: Array<{
    operation: 'add' | 'update' | 'delete'
    section: string
    content?: string
    card_source?: string
  }>
  cards_integrated?: string[]
  cards_referenced?: string[]
  phase?: string
  session_context?: Record<string, unknown>
  foolscap_json?: FoolscapJson
  foolscap_markdown?: string
  foolscap_markdown_annotated?: string
  session_id?: string
}

type WorkflowStatus = 'idle' | 'submitting' | 'processing' | 'streaming' | 'complete' | 'error'
export type FPhase = 'discovery' | 'structuring' | 'refinement'

interface FState {
  // Session
  sessionId: string | null
  sessionName: string

  // Entity context
  entityName: string
  entityType: string

  // Session list (for sidebar)
  sessions: FSessionListItem[]
  isLoadingSessions: boolean

  // Session sidebar (left panel)
  showSessionSidebar: boolean

  // Mobile view state
  mobileView: 'sessions' | 'chat' | 'document'

  // Conversation
  turns: Turn[]

  // Workflow status
  workflowStatus: WorkflowStatus
  error: string | null

  // Phase (discovery -> structuring -> refinement)
  phase: FPhase

  // Streaming state (legacy single-node)
  streamingThinking: string | null
  isThinking: boolean
  streamingResponse: string

  // Per-node streaming state - Gatekeeper
  gatekeeperIsProcessing: boolean
  gatekeeperIsThinking: boolean
  gatekeeperHasThinking: boolean
  gatekeeperStreamingThinking: string | null

  // Per-node streaming state - Evaluator
  evaluatorIsProcessing: boolean
  evaluatorIsThinking: boolean
  evaluatorHasThinking: boolean
  evaluatorStreamingThinking: string | null

  editorIsProcessing: boolean
  editorIsThinking: boolean
  editorHasThinking: boolean
  editorStreamingThinking: string | null

  responderIsProcessing: boolean
  responderIsThinking: boolean
  responderHasThinking: boolean
  responderStreamingThinking: string | null

  // Foolscap document
  foolscapJson: FoolscapJson | null
  foolscapMarkdown: string
  foolscapMarkdownAnnotated: string | null
  showAnnotations: boolean

  // Card retrieval toggle (can be flipped mid-session)
  cardsEnabled: boolean

  // Surfaced cards (from RetrieveCardsNode)
  surfacedCards: RetrievedCard[]

  // Integrated card IDs (cards woven into document)
  integratedCardIds: string[]

  // Selected card ID (for navigating from document to cards panel)
  selectedCardId: string | null

  // Attachment version (for triggering refresh after upload)
  attachmentVersion: number

  // Session context (from composer)
  sessionContext: Record<string, unknown>

  // Actions
  setSessionId: (id: string | null) => void
  setSessionName: (name: string) => void
  setEntityName: (name: string) => void
  setEntityType: (type: string) => void
  setSessions: (sessions: FSessionListItem[]) => void
  setIsLoadingSessions: (loading: boolean) => void
  toggleSessionSidebar: () => void
  closeSessionSidebar: () => void
  setMobileView: (view: 'sessions' | 'chat' | 'document') => void
  setPhase: (phase: FPhase) => void

  // Session management
  createNewSession: (entityName?: string, entityType?: string) => Promise<void>
  loadSessionFromDb: (sessionId: string) => Promise<void>
  loadSessions: () => Promise<void>
  deleteSession: (sessionId: string) => Promise<void>
  renameSession: (name: string) => Promise<void>
  startNewSession: () => void

  // Conversation actions
  addUserTurn: (content: string) => void
  startStreaming: () => void
  startThinking: () => void
  updateThinking: (delta: string) => void
  finishThinking: () => void
  updateStreamingResponse: (delta: string) => void
  finishStreaming: (response: ComposerResponse) => void

  // Per-node actions - Gatekeeper
  startGatekeeperProcessing: () => void
  startGatekeeperThinking: () => void
  updateGatekeeperThinking: (delta: string) => void
  finishGatekeeperThinking: () => void
  finishGatekeeperProcessing: () => void

  // Per-node actions - Evaluator
  startEvaluatorProcessing: () => void
  startEvaluatorThinking: () => void
  updateEvaluatorThinking: (delta: string) => void
  finishEvaluatorThinking: () => void
  finishEvaluatorProcessing: () => void

  // Per-node actions - Editor
  startEditorProcessing: () => void
  startEditorThinking: () => void
  updateEditorThinking: (delta: string) => void
  finishEditorThinking: () => void
  finishEditorProcessing: () => void

  // Per-node actions - Responder
  startResponderProcessing: () => void
  startResponderThinking: () => void
  updateResponderThinking: (delta: string) => void
  finishResponderThinking: () => void
  finishResponderProcessing: () => void

  // Reset per-node state (call at start of new turn)
  resetNodeState: () => void

  // Workflow status
  setWorkflowStatus: (status: WorkflowStatus) => void
  setError: (error: string | null) => void

  // Card actions
  setSurfacedCards: (cards: RetrievedCard[]) => void
  addIntegratedCard: (cardId: string) => void

  // Document actions
  updateFoolscapJson: (json: FoolscapJson) => void
  updateFoolscapMarkdown: (markdown: string) => void
  updateFoolscapMarkdownAnnotated: (markdown: string | null) => void
  toggleAnnotations: () => void
  toggleCardsEnabled: () => void
  updateSessionContext: (context: Record<string, unknown>) => void

  // Card navigation
  setSelectedCardId: (cardId: string | null) => void

  // Attachments
  incrementAttachmentVersion: () => void
}

// =============================================================================
// Store
// =============================================================================

export const useFStore = create<FState>()(
  persist(
    (set, get) => ({
      // Initial state
      sessionId: null,
      sessionName: 'New Foolscap',
      entityName: '',
      entityType: '',
      sessions: [],
      isLoadingSessions: false,
      showSessionSidebar: false,
      mobileView: 'chat',
      turns: [],
      workflowStatus: 'idle',
      error: null,
      phase: 'discovery',
      streamingThinking: null,
      isThinking: false,
      streamingResponse: '',
      // Per-node streaming state
      gatekeeperIsProcessing: false,
      gatekeeperIsThinking: false,
      gatekeeperHasThinking: false,
      gatekeeperStreamingThinking: null,
      evaluatorIsProcessing: false,
      evaluatorIsThinking: false,
      evaluatorHasThinking: false,
      evaluatorStreamingThinking: null,
      editorIsProcessing: false,
      editorIsThinking: false,
      editorHasThinking: false,
      editorStreamingThinking: null,
      responderIsProcessing: false,
      responderIsThinking: false,
      responderHasThinking: false,
      responderStreamingThinking: null,
      foolscapJson: null,
      foolscapMarkdown: '',
      foolscapMarkdownAnnotated: null,
      showAnnotations: true,  // Default to showing annotations
      cardsEnabled: true,  // Default to card retrieval enabled
      surfacedCards: [],
      integratedCardIds: [],
      selectedCardId: null,
      attachmentVersion: 0,
      sessionContext: {},

      // Actions
      setSessionId: (id) => set({ sessionId: id }),
      setSessionName: (name) => set({ sessionName: name }),
      setEntityName: (name) => set({ entityName: name }),
      setEntityType: (type) => set({ entityType: type }),
      setSessions: (sessions) => set({ sessions }),
      setIsLoadingSessions: (loading) => set({ isLoadingSessions: loading }),
      toggleSessionSidebar: () => set((state) => ({ showSessionSidebar: !state.showSessionSidebar })),
      closeSessionSidebar: () => set({ showSessionSidebar: false }),
      setMobileView: (view) => set({ mobileView: view }),
      setPhase: (phase) => set({ phase }),

      // Session management
      createNewSession: async (entityName?: string, entityType?: string) => {
        try {
          const session = await createFSession(undefined, entityName, entityType)
          set({
            sessionId: session.session_id,
            sessionName: session.name || 'New Foolscap',
            entityName: session.entity_name || entityName || '',
            entityType: session.entity_type || entityType || '',
            turns: [],
            workflowStatus: 'idle',
            error: null,
            phase: 'discovery',
            foolscapJson: session.foolscap_json || null,
            foolscapMarkdown: session.foolscap_markdown || '',
            foolscapMarkdownAnnotated: session.foolscap_markdown_annotated || null,
            surfacedCards: [],
            integratedCardIds: session.integrated_card_ids || [],
            sessionContext: session.session_context || {},
          })
          // Refresh session list
          get().loadSessions()
        } catch (e) {
          console.error('[f-store] Failed to create session:', e)
          set({ error: 'Failed to create session' })
        }
      },

      loadSessionFromDb: async (sessionId: string) => {
        try {
          const session = await getFSessionWithTurns(sessionId)
          const turns: Turn[] = session.turns?.map((t) => ({
            role: t.role,
            content: t.content,
            timestamp: t.timestamp,
            thinking: t.thinking || undefined,
            cardsSurfaced: t.cards_surfaced || undefined,
            cardsUsed: t.cards_used as Turn['cardsUsed'],
          })) || []

          // Determine phase from session context
          const contextPhase = session.session_context?.phase as FPhase | undefined
          const phase: FPhase = contextPhase || (session.foolscap_json ? 'structuring' : 'discovery')

          // Set session state immediately (cards will load async)
          set({
            sessionId: session.session_id,
            sessionName: session.name || 'New Foolscap',
            entityName: session.entity_name || '',
            entityType: session.entity_type || '',
            turns,
            workflowStatus: 'idle',
            error: null,
            phase,
            foolscapJson: session.foolscap_json || null,
            foolscapMarkdown: session.foolscap_markdown || '',
            foolscapMarkdownAnnotated: session.foolscap_markdown_annotated || null,
            surfacedCards: [],
            integratedCardIds: session.integrated_card_ids || [],
            sessionContext: session.session_context || {},
          })

          // Fetch surfaced cards in background (don't block session load)
          const surfacedIds = session.surfaced_card_ids || []
          if (surfacedIds.length > 0) {
            Promise.all(
              surfacedIds.map((id) =>
                fetchCard(id)
                  .then((card) => ({
                    card_id: card.card_id,
                    type: card.type || '',
                    domain: card.domain || '',
                    title: card.title || null,
                    summary: card.summary || '',
                    tags: card.tags || [],
                    quality_score: card.quality_score || 0,
                    support_count: 0,
                    similarity: 1.0, // No similarity score for restored cards
                  } as RetrievedCard))
                  .catch(() => null)
              )
            ).then((results) => {
              const cards = results.filter((c): c is RetrievedCard => c !== null)
              if (cards.length > 0) {
                set({ surfacedCards: cards })
              }
            })
          }
        } catch (e) {
          console.error('[f-store] Failed to load session:', e)
          // Create new session on failure
          get().createNewSession()
        }
      },

      loadSessions: async () => {
        set({ isLoadingSessions: true })
        try {
          const sessions = await listFSessions()
          set({ sessions, isLoadingSessions: false })
        } catch (e) {
          console.error('[f-store] Failed to load sessions:', e)
          set({ isLoadingSessions: false })
        }
      },

      deleteSession: async (sessionId: string) => {
        try {
          await deleteFSession(sessionId)
          // Refresh session list
          get().loadSessions()
          // If deleted current session, create new one
          if (get().sessionId === sessionId) {
            get().createNewSession()
          }
        } catch (e) {
          console.error('[f-store] Failed to delete session:', e)
        }
      },

      renameSession: async (name: string) => {
        const sessionId = get().sessionId
        if (!sessionId) return
        try {
          await updateFSession(sessionId, { name })
          set({ sessionName: name })
          get().loadSessions()
        } catch (e) {
          console.error('[f-store] Failed to rename session:', e)
        }
      },

      startNewSession: () => {
        set({
          sessionId: null,
          sessionName: 'New Foolscap',
          entityName: '',
          entityType: '',
          turns: [],
          workflowStatus: 'idle',
          error: null,
          phase: 'discovery',
          streamingThinking: null,
          isThinking: false,
          streamingResponse: '',
          foolscapJson: null,
          foolscapMarkdown: '',
          foolscapMarkdownAnnotated: null,
          surfacedCards: [],
          integratedCardIds: [],
          selectedCardId: null,
          sessionContext: {},
        })
      },

      // Conversation actions
      addUserTurn: (content: string) => {
        const turn: Turn = {
          role: 'user',
          content,
          timestamp: new Date().toISOString(),
        }
        set((state) => ({ turns: [...state.turns, turn] }))
      },

      startStreaming: () => {
        set({
          workflowStatus: 'streaming',
          streamingResponse: '',
          streamingThinking: null,
          isThinking: false,
        })
      },

      startThinking: () => {
        set({ isThinking: true, streamingThinking: '' })
      },

      updateThinking: (delta: string) => {
        set((state) => ({
          streamingThinking: (state.streamingThinking || '') + delta,
        }))
      },

      finishThinking: () => {
        set({ isThinking: false })
      },

      // Per-node actions - Evaluator
      // Per-node actions - Gatekeeper
      startGatekeeperProcessing: () =>
        set({
          gatekeeperIsProcessing: true,
          gatekeeperHasThinking: true,
          gatekeeperStreamingThinking: '',
          workflowStatus: 'processing',
        }),

      startGatekeeperThinking: () =>
        set({
          gatekeeperIsThinking: true,
          gatekeeperStreamingThinking: '',
        }),

      updateGatekeeperThinking: (delta: string) =>
        set((state) => ({
          gatekeeperStreamingThinking: (state.gatekeeperStreamingThinking || '') + delta,
        })),

      finishGatekeeperThinking: () =>
        set({ gatekeeperIsThinking: false }),

      finishGatekeeperProcessing: () =>
        set({
          gatekeeperIsProcessing: false,
          gatekeeperIsThinking: false,
        }),

      // Per-node actions - Evaluator
      startEvaluatorProcessing: () =>
        set({
          evaluatorIsProcessing: true,
          evaluatorHasThinking: true,
          evaluatorStreamingThinking: '',
          workflowStatus: 'processing',
        }),

      startEvaluatorThinking: () =>
        set({
          evaluatorIsThinking: true,
          evaluatorStreamingThinking: '',
        }),

      updateEvaluatorThinking: (delta: string) =>
        set((state) => ({
          evaluatorStreamingThinking: (state.evaluatorStreamingThinking || '') + delta,
        })),

      finishEvaluatorThinking: () =>
        set({ evaluatorIsThinking: false }),

      finishEvaluatorProcessing: () =>
        set({
          evaluatorIsProcessing: false,
          evaluatorIsThinking: false,
        }),

      // Per-node actions - Editor
      startEditorProcessing: () =>
        set({
          editorIsProcessing: true,
          editorHasThinking: true,
          editorStreamingThinking: '',
        }),

      startEditorThinking: () =>
        set({
          editorIsThinking: true,
          editorStreamingThinking: '',
        }),

      updateEditorThinking: (delta: string) =>
        set((state) => ({
          editorStreamingThinking: (state.editorStreamingThinking || '') + delta,
        })),

      finishEditorThinking: () =>
        set({ editorIsThinking: false }),

      finishEditorProcessing: () =>
        set({
          editorIsProcessing: false,
          editorIsThinking: false,
        }),

      // Per-node actions - Responder
      startResponderProcessing: () =>
        set({
          responderIsProcessing: true,
          responderHasThinking: true,
          responderStreamingThinking: '',
        }),

      startResponderThinking: () =>
        set({
          responderIsThinking: true,
          responderStreamingThinking: '',
        }),

      updateResponderThinking: (delta: string) =>
        set((state) => ({
          responderStreamingThinking: (state.responderStreamingThinking || '') + delta,
        })),

      finishResponderThinking: () =>
        set({ responderIsThinking: false }),

      finishResponderProcessing: () =>
        set({
          responderIsProcessing: false,
          responderIsThinking: false,
        }),

      // Reset per-node state
      resetNodeState: () =>
        set({
          gatekeeperIsProcessing: false,
          gatekeeperIsThinking: false,
          gatekeeperHasThinking: false,
          gatekeeperStreamingThinking: null,
          evaluatorIsProcessing: false,
          evaluatorIsThinking: false,
          evaluatorHasThinking: false,
          evaluatorStreamingThinking: null,
          editorIsProcessing: false,
          editorIsThinking: false,
          editorHasThinking: false,
          editorStreamingThinking: null,
          responderIsProcessing: false,
          responderIsThinking: false,
          responderHasThinking: false,
          responderStreamingThinking: null,
        }),

      updateStreamingResponse: (delta: string) => {
        set((state) => ({
          streamingResponse: state.streamingResponse + delta,
        }))
      },

      finishStreaming: (response: ComposerResponse) => {
        const state = get()

        // Build turn with per-node thinking
        const turn: Turn = {
          role: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString(),
          thinking: response.thinking,
          cardsUsed: {
            integrated: response.cards_integrated,
            referenced: response.cards_referenced,
          },
          // Per-node thinking (transfer from streaming state)
          evaluatorThinking: state.evaluatorStreamingThinking || undefined,
          editorThinking: state.editorStreamingThinking || undefined,
          responderThinking: state.responderStreamingThinking || undefined,
          // Node execution flags
          evaluatorRan: state.evaluatorHasThinking,
          editorRan: state.editorHasThinking,
          responderRan: state.responderHasThinking,
        }

        // Determine new phase
        const newPhase = (response.phase as FPhase) || state.phase

        // Update integrated cards
        const existingIntegrated = state.integratedCardIds
        const newIntegrated = response.cards_integrated || []
        const allIntegrated = [...new Set([...existingIntegrated, ...newIntegrated])]

        set({
          turns: [...state.turns, turn],
          workflowStatus: 'idle',
          streamingResponse: '',
          streamingThinking: null,
          isThinking: false,
          phase: newPhase,
          foolscapJson: response.foolscap_json || state.foolscapJson,
          foolscapMarkdown: response.foolscap_markdown || state.foolscapMarkdown,
          foolscapMarkdownAnnotated: response.foolscap_markdown_annotated || state.foolscapMarkdownAnnotated,
          sessionContext: response.session_context || state.sessionContext,
          integratedCardIds: allIntegrated,
          sessionId: response.session_id || state.sessionId,
          // Reset per-node state
          gatekeeperIsProcessing: false,
          gatekeeperIsThinking: false,
          gatekeeperHasThinking: false,
          gatekeeperStreamingThinking: null,
          evaluatorIsProcessing: false,
          evaluatorIsThinking: false,
          evaluatorHasThinking: false,
          evaluatorStreamingThinking: null,
          editorIsProcessing: false,
          editorIsThinking: false,
          editorHasThinking: false,
          editorStreamingThinking: null,
          responderIsProcessing: false,
          responderIsThinking: false,
          responderHasThinking: false,
          responderStreamingThinking: null,
        })
      },

      // Workflow status
      setWorkflowStatus: (status) => set({ workflowStatus: status }),
      setError: (error) => set({ error, workflowStatus: error ? 'error' : 'idle' }),

      // Card actions
      setSurfacedCards: (cards) => set((state) => {
        // Merge new cards with existing, keeping highest similarity for duplicates
        const cardMap = new Map<string, RetrievedCard>()

        // Add existing cards first
        for (const card of state.surfacedCards) {
          cardMap.set(card.card_id, card)
        }

        // Merge new cards, keeping higher similarity
        for (const card of cards) {
          const existing = cardMap.get(card.card_id)
          if (!existing || card.similarity > existing.similarity) {
            cardMap.set(card.card_id, card)
          }
        }

        // Sort by similarity descending
        const merged = Array.from(cardMap.values()).sort(
          (a, b) => b.similarity - a.similarity
        )

        return { surfacedCards: merged }
      }),
      addIntegratedCard: (cardId) => set((state) => ({
        integratedCardIds: [...new Set([...state.integratedCardIds, cardId])],
      })),

      // Document actions
      updateFoolscapJson: (json) => set({ foolscapJson: json }),
      updateFoolscapMarkdown: (markdown) => set({ foolscapMarkdown: markdown }),
      updateFoolscapMarkdownAnnotated: (markdown) => set({ foolscapMarkdownAnnotated: markdown }),
      toggleAnnotations: () => set((state) => ({ showAnnotations: !state.showAnnotations })),
      toggleCardsEnabled: () => set((state) => ({ cardsEnabled: !state.cardsEnabled })),
      updateSessionContext: (context) => set({ sessionContext: context }),

      // Card navigation
      setSelectedCardId: (cardId) => set({ selectedCardId: cardId }),

      // Attachments
      incrementAttachmentVersion: () => set((state) => ({ attachmentVersion: state.attachmentVersion + 1 })),
    }),
    {
      name: 'f-store',
      partialize: (state) => ({
        // Only persist these fields
        sessionId: state.sessionId,
        sessionName: state.sessionName,
        entityName: state.entityName,
        entityType: state.entityType,
        showSessionSidebar: state.showSessionSidebar,
        mobileView: state.mobileView,
      }),
    }
  )
)

// =============================================================================
// SSE Streaming API
// =============================================================================

export interface FStreamParams {
  content: string
  session_id: string
  current_foolscap_json?: FoolscapJson | null
  entity_name?: string
  entity_type?: string
  cards_enabled?: boolean
}

export interface DiffStats {
  blocks_added: number
  blocks_deleted: number
  blocks_modified: number
  blocks_unchanged: number
}

export interface FStreamCallbacks {
  // Legacy single-node callbacks (still work for backwards compat)
  onThinkingStart?: () => void
  onThinkingDelta?: (delta: string) => void
  onThinkingComplete?: () => void
  onResponseDelta?: (delta: string) => void
  onCardsSurfaced?: (cards: RetrievedCard[]) => void
  onDocumentUpdate?: (json: FoolscapJson, markdown: string, annotatedMarkdown: string | null) => void
  onEditsComputed?: (annotatedDoc: string, stats: DiffStats) => void  // NEW: Edits tab content
  onDone?: (output: ComposerResponse) => void
  onError: (error: string) => void

  // Per-node callbacks - Gatekeeper
  onGatekeeperStart?: () => void
  onGatekeeperThinkingStart?: () => void
  onGatekeeperThinkingDelta?: (delta: string) => void
  onGatekeeperThinkingComplete?: () => void
  onGatekeeperDone?: () => void

  // Per-node callbacks - Evaluator
  onEvaluatorStart?: () => void
  onEvaluatorThinkingStart?: () => void
  onEvaluatorThinkingDelta?: (delta: string) => void
  onEvaluatorThinkingComplete?: () => void
  onEvaluatorDone?: () => void

  // Per-node callbacks - Editor
  onEditorStart?: () => void
  onEditorThinkingStart?: () => void
  onEditorThinkingDelta?: (delta: string) => void
  onEditorThinkingComplete?: () => void
  onEditorDone?: () => void

  // Per-node callbacks - Responder
  onResponderStart?: () => void
  onResponderThinkingStart?: () => void
  onResponderThinkingDelta?: (delta: string) => void
  onResponderThinkingComplete?: () => void
  onResponderDone?: () => void
}

export async function streamF(
  params: FStreamParams,
  callbacks: FStreamCallbacks
): Promise<void> {
  const authHeaders = await getAuthHeaders()
  const response = await fetch(`${API_BASE}/f/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/z/login?returnTo=' + encodeURIComponent(window.location.pathname)
      callbacks.onError('Authentication required')
      return
    }
    callbacks.onError(`HTTP ${response.status}: ${response.statusText}`)
    return
  }

  const reader = response.body?.getReader()
  if (!reader) {
    callbacks.onError('No response body')
    return
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '[DONE]') continue

        try {
          const event = JSON.parse(data)

          switch (event.type) {
            // Legacy single-node events (backwards compat)
            case 'thinking_start':
              callbacks.onThinkingStart?.()
              break
            case 'thinking':
              callbacks.onThinkingDelta?.(event.content || '')
              break
            case 'thinking_complete':
              callbacks.onThinkingComplete?.()
              break

            // Gatekeeper events
            case 'gatekeeper_start':
              callbacks.onGatekeeperStart?.()
              break
            case 'gatekeeper_thinking_start':
              callbacks.onGatekeeperThinkingStart?.()
              break
            case 'gatekeeper_thinking':
              callbacks.onGatekeeperThinkingDelta?.(event.content || '')
              break
            case 'gatekeeper_thinking_complete':
              callbacks.onGatekeeperThinkingComplete?.()
              break
            case 'gatekeeper_done':
              callbacks.onGatekeeperDone?.()
              break

            // Evaluator events
            case 'evaluator_start':
              callbacks.onEvaluatorStart?.()
              break
            case 'evaluator_thinking_start':
              callbacks.onEvaluatorThinkingStart?.()
              break
            case 'evaluator_thinking':
              callbacks.onEvaluatorThinkingDelta?.(event.content || '')
              break
            case 'evaluator_thinking_complete':
              callbacks.onEvaluatorThinkingComplete?.()
              break
            case 'evaluator_done':
              callbacks.onEvaluatorDone?.()
              break

            // Editor events
            case 'editor_start':
              callbacks.onEditorStart?.()
              break
            case 'editor_thinking_start':
              callbacks.onEditorThinkingStart?.()
              break
            case 'editor_thinking':
              callbacks.onEditorThinkingDelta?.(event.content || '')
              break
            case 'editor_thinking_complete':
              callbacks.onEditorThinkingComplete?.()
              break
            case 'editor_done':
              callbacks.onEditorDone?.()
              break

            // Responder events
            case 'responder_start':
              callbacks.onResponderStart?.()
              break
            case 'responder_thinking_start':
              callbacks.onResponderThinkingStart?.()
              break
            case 'responder_thinking':
              callbacks.onResponderThinkingDelta?.(event.content || '')
              break
            case 'responder_thinking_complete':
              callbacks.onResponderThinkingComplete?.()
              break
            case 'responder_done':
              callbacks.onResponderDone?.()
              break

            // Response and completion
            case 'response':
              callbacks.onResponseDelta?.(event.text || '')
              break
            case 'cards_surfaced':
              callbacks.onCardsSurfaced?.(event.cards || [])
              break
            case 'document_update':
              callbacks.onDocumentUpdate?.(event.foolscap_json, event.foolscap_markdown, event.foolscap_markdown_annotated || null)
              break
            case 'edits_computed':
              // NEW: Edits tab content from FFinalDiffNode
              callbacks.onEditsComputed?.(event.annotated_doc || '', event.stats || {})
              break
            case 'done':
              callbacks.onDone?.(event.output || event)
              break
            case 'error':
              callbacks.onError(event.message || 'Unknown error')
              break
          }
        } catch (e) {
          console.error('[streamF] Failed to parse event:', e, data)
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}
